/**
 * Options WebSocket Market Streams
 *
 * Access market data, manage accounts, and trade Binance Options.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { WebsocketStreamsBase, WebsocketStream, createStreamHandler } from '@binance/common';
import { MarketApi } from './modules/market-api';
import { PublicApi } from './modules/public-api';

import type { UserDataStreamEventsResponse } from './types';

import type {
    IndexPriceStreamsRequest,
    KlineCandlestickStreamsRequest,
    NewSymbolInfoRequest,
    OpenInterestRequest,
    OptionMarkPriceRequest,
} from './modules/market-api';
import type {
    DiffBookDepthStreamsRequest,
    Hour24TickerRequest,
    IndividualSymbolBookTickerStreamsRequest,
    PartialBookDepthStreamsRequest,
    TradeStreamsRequest,
} from './modules/public-api';

import type {
    IndexPriceStreamsResponse,
    KlineCandlestickStreamsResponse,
    NewSymbolInfoResponse,
    OpenInterestResponse,
    OptionMarkPriceResponse,
} from './types';
import type {
    DiffBookDepthStreamsResponse,
    Hour24TickerResponse,
    IndividualSymbolBookTickerStreamsResponse,
    PartialBookDepthStreamsResponse,
    TradeStreamsResponse,
} from './types';

export class WebsocketStreamsConnection {
    private websocketBase: WebsocketStreamsBase;
    private marketApi: MarketApi;
    private publicApi: PublicApi;

    constructor(websocketBase: WebsocketStreamsBase) {
        this.websocketBase = websocketBase;
        this.marketApi = new MarketApi(websocketBase);
        this.publicApi = new PublicApi(websocketBase);
    }

    /**
     * Adds an event listener for the specified WebSocket event.
     * @param event - The WebSocket event to listen for, such as 'open', 'message', 'error', 'close', 'ping', or 'pong'.
     * @param listener - The callback function to be executed when the event is triggered. The function can accept any number of arguments.
     */
    on(
        event: 'open' | 'message' | 'error' | 'close' | 'ping' | 'pong',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        listener: (...args: any[]) => void
    ) {
        this.websocketBase.on(event, listener);
    }

    /**
     * Removes an event listener for the specified WebSocket event.
     * @param event - The WebSocket event to stop listening for, such as 'open', 'message', 'error', 'close', 'ping', or 'pong'.
     * @param listener - The callback function that was previously added as the event listener.
     */
    off(
        event: 'open' | 'message' | 'error' | 'close' | 'ping' | 'pong',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        listener: (...args: any[]) => void
    ) {
        this.websocketBase.off(event, listener);
    }

    /**
     * Disconnects from the WebSocket server.
     * If there is no active connection, a warning is logged.
     * Otherwise, all connections in the connection pool are closed gracefully,
     * and a message is logged indicating that the connection has been disconnected.
     * @returns A Promise that resolves when all connections have been closed.
     * @throws Error if the WebSocket client is not set.
     */
    disconnect(): Promise<void> {
        return this.websocketBase.disconnect();
    }

    /**
     * Checks if the WebSocket connection is currently open.
     * @returns `true` if the connection is open, `false` otherwise.
     */
    isConnected(): boolean {
        return this.websocketBase.isConnected();
    }

    /**
     * Sends a ping message to all connected Websocket servers in the pool.
     * If no connections are ready, a warning is logged.
     * For each active connection, the ping message is sent, and debug logs provide details.
     * @throws Error if a Websocket client is not set for a connection.
     */
    pingServer(): void {
        this.websocketBase.pingServer();
    }

    /**
     * Subscribes to one or multiple WebSocket streams
     * Handles both single and pool modes
     * @param stream Single stream name or array of stream names to subscribe to
     * @param id Optional subscription ID
     * @returns void
     */
    subscribe(stream: string | string[], id?: number): void {
        this.websocketBase.subscribe(stream, id);
    }

    /**
     * Unsubscribes from one or multiple WebSocket streams
     * Handles both single and pool modes
     * @param stream Single stream name or array of stream names to unsubscribe from
     * @param id Optional unsubscription ID
     * @returns void
     */
    unsubscribe(stream: string | string[], id?: number): void {
        this.websocketBase.unsubscribe(stream, id);
    }

    /**
     * Checks if the WebSocket connection is subscribed to the specified stream.
     * @param stream The name of the WebSocket stream to check.
     * @returns `true` if the connection is subscribed to the stream, `false` otherwise.
     */
    isSubscribed(stream: string): boolean {
        return this.websocketBase.isSubscribed(stream);
    }

    /**
     * Subscribes to the user data WebSocket stream using the provided listen key.
     * @param listenKey - The listen key for the user data WebSocket stream.
     * @param id - Optional user data stream ID
     * @returns A WebSocket stream handler for the user data stream.
     */
    userData(listenKey: string, id?: string): WebsocketStream<UserDataStreamEventsResponse> {
        return createStreamHandler<UserDataStreamEventsResponse>(
            this.websocketBase,
            listenKey,
            id,
            'private'
        );
    }

    /**
     * Underlying(e.g ETHUSDT) index stream.
     *
     * Update Speed: 1000ms
     *
     * @summary Index Price Streams
     * @param {IndexPriceStreamsRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<IndexPriceStreamsResponse>}
     * @throws {RequiredError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/ws-streams/market#index-price-streams Binance API Documentation}
     */
    indexPriceStreams(
        requestParameters: IndexPriceStreamsRequest = {}
    ): WebsocketStream<IndexPriceStreamsResponse> {
        return this.marketApi.indexPriceStreams(requestParameters);
    }

    /**
     * The Kline/Candlestick Stream push updates to the current klines/candlestick every 1000 milliseconds (if existing).
     *
     * Update Speed: 1000ms
     *
     * @summary Kline/Candlestick Streams
     * @param {KlineCandlestickStreamsRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<KlineCandlestickStreamsResponse>}
     * @throws {RequiredError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/ws-streams/market#kline-candlestick-streams Binance API Documentation}
     */
    klineCandlestickStreams(
        requestParameters: KlineCandlestickStreamsRequest
    ): WebsocketStream<KlineCandlestickStreamsResponse> {
        return this.marketApi.klineCandlestickStreams(requestParameters);
    }

    /**
     * New symbol listing stream.
     *
     * Update Speed: 50ms
     *
     * @summary New Symbol Info
     * @param {NewSymbolInfoRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<NewSymbolInfoResponse>}
     * @throws {RequiredError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/ws-streams/market#new-symbol-info Binance API Documentation}
     */
    newSymbolInfo(
        requestParameters: NewSymbolInfoRequest = {}
    ): WebsocketStream<NewSymbolInfoResponse> {
        return this.marketApi.newSymbolInfo(requestParameters);
    }

    /**
     * Option open interest for specific underlying asset on specific expiration date. E.g.[ethusdt@openInterest@221125](wss://fstream.binance.com/market/stream?streams=ethusdt@openInterest@221125)
     *
     * Update Speed: 60s
     *
     * @summary Open Interest
     * @param {OpenInterestRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<OpenInterestResponse>}
     * @throws {RequiredError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/ws-streams/market#open-interest Binance API Documentation}
     */
    openInterest(requestParameters: OpenInterestRequest): WebsocketStream<OpenInterestResponse> {
        return this.marketApi.openInterest(requestParameters);
    }

    /**
     * The mark price for all option symbols on specific underlying asset. E.g.[btcusdt@optionMarkPrice](wss://fstream.binance.com/market/stream?streams=btcusdt@optionMarkPrice)
     *
     * Update Speed: 1000ms
     *
     * @summary Option Mark Price
     * @param {OptionMarkPriceRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<OptionMarkPriceResponse>}
     * @throws {RequiredError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/ws-streams/market#option-mark-price Binance API Documentation}
     */
    optionMarkPrice(
        requestParameters: OptionMarkPriceRequest
    ): WebsocketStream<OptionMarkPriceResponse> {
        return this.marketApi.optionMarkPrice(requestParameters);
    }

    /**
     * Bids and asks, pushed every 500 milliseconds, 100 milliseconds (if existing)
     *
     * Update Speed: 100ms or 500ms
     *
     * @summary Diff Book Depth Streams
     * @param {DiffBookDepthStreamsRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<DiffBookDepthStreamsResponse>}
     * @throws {RequiredError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/ws-streams/public#diff-book-depth-streams Binance API Documentation}
     */
    diffBookDepthStreams(
        requestParameters: DiffBookDepthStreamsRequest
    ): WebsocketStream<DiffBookDepthStreamsResponse> {
        return this.publicApi.diffBookDepthStreams(requestParameters);
    }

    /**
     * 24hr ticker info for all symbols. Only symbols whose ticker info changed will be sent.
     *
     * Update Speed: 1000ms
     *
     * @summary 24-hour TICKER
     * @param {Hour24TickerRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<Hour24TickerResponse>}
     * @throws {RequiredError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/ws-streams/public#hour24-ticker Binance API Documentation}
     */
    hour24Ticker(requestParameters: Hour24TickerRequest): WebsocketStream<Hour24TickerResponse> {
        return this.publicApi.hour24Ticker(requestParameters);
    }

    /**
     * Pushes any update to the best bid or ask's price or quantity in real-time for a specified symbol.
     *
     * Update Speed: Real-Time
     *
     * @summary Individual Symbol Book Ticker Streams
     * @param {IndividualSymbolBookTickerStreamsRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<IndividualSymbolBookTickerStreamsResponse>}
     * @throws {RequiredError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/ws-streams/public#individual-symbol-book-ticker-streams Binance API Documentation}
     */
    individualSymbolBookTickerStreams(
        requestParameters: IndividualSymbolBookTickerStreamsRequest
    ): WebsocketStream<IndividualSymbolBookTickerStreamsResponse> {
        return this.publicApi.individualSymbolBookTickerStreams(requestParameters);
    }

    /**
     * Top <levels> bids and asks. Valid <levels> are 5, 10, 20.
     *
     * Update Speed: 100ms or 500ms
     *
     * @summary Partial Book Depth Streams
     * @param {PartialBookDepthStreamsRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<PartialBookDepthStreamsResponse>}
     * @throws {RequiredError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/ws-streams/public#partial-book-depth-streams Binance API Documentation}
     */
    partialBookDepthStreams(
        requestParameters: PartialBookDepthStreamsRequest
    ): WebsocketStream<PartialBookDepthStreamsResponse> {
        return this.publicApi.partialBookDepthStreams(requestParameters);
    }

    /**
     * The Trade Streams push raw trade information for specific symbol or underlying asset. E.g.[btcusdt@optionTrade](wss://fstream.binance.com/public/stream?streams=btcusdt@optionTrade)
     *
     * Update Speed: 50ms
     *
     * @summary Trade Streams
     * @param {TradeStreamsRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<TradeStreamsResponse>}
     * @throws {RequiredError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/ws-streams/public#trade-streams Binance API Documentation}
     */
    tradeStreams(requestParameters: TradeStreamsRequest): WebsocketStream<TradeStreamsResponse> {
        return this.publicApi.tradeStreams(requestParameters);
    }
}
